import { Decimal } from 'decimal.js';
import type { EdiCatalogItem, EdiPurchaseOrderItem } from '@/api/hanly-v2/hanlyAPIV2.schemas';
import {
  calculateItemAmount,
  type OrderItemDisplayData,
  type QuantityPriceTier,
  resolveUnitPrice,
} from './types';

const toQuantityPriceTiers = (
  quantityPrices:
    | readonly { min_quantity: number; max_quantity: number | null; price: string }[]
    | null
    | undefined,
): QuantityPriceTier[] =>
  (quantityPrices ?? []).map((qp) => ({
    minQuantity: qp.min_quantity,
    maxQuantity: qp.max_quantity,
    price: qp.price,
  }));

/** カタログの商品から新規発注の明細表示データを作る。単価は数量別単価を反映した現在の価格 */
export const toCatalogItemDisplayData = (
  catalogItem: EdiCatalogItem,
  quantity: number,
  desiredDeliveryDate: string | null,
  previousUnitPrice: string | null,
): OrderItemDisplayData => {
  const pv = catalogItem.product_variation;
  // 料金を出さない発注先では単価が null になる(画面では料金を出さない)
  const baseUnitPrice = pv.base_unit_price ?? pv.unit_price ?? '0';
  const quantityPrices = toQuantityPriceTiers(pv.quantity_prices);
  const unitPrice = resolveUnitPrice(baseUnitPrice, quantityPrices, quantity);
  return {
    key: pv.id,
    productCode: pv.code,
    modelNumber: pv.model_number,
    name: pv.name,
    manufacturer: pv.manufacturer ?? null,
    spec: pv.spec,
    unitPrice,
    baseUnitPrice,
    quantityPrices,
    quantity,
    snp: Number(pv.snp),
    packagingTypeName: pv.packaging_type_name ?? null,
    unitLabel: pv.unit_name ?? null,
    amount: calculateItemAmount(unitPrice, quantity),
    taxClass: pv.tax_class,
    lastPurchasedAt: null,
    desiredDeliveryDate,
    thumbnailUrl: pv.thumbnail_url ?? null,
    previousUnitPrice,
  };
};

/**
 * 発注の明細から表示データを作る。
 * 数量を変えた明細だけ最新の数量別単価で再計算し、変えていなければ保存済みの単価を使う。
 */
export const toOrderItemDisplayData = (
  item: EdiPurchaseOrderItem,
  quantity: number,
  desiredDeliveryDate: string | null,
): OrderItemDisplayData => {
  // 荷姿換算・入数表示は商品マスタの入数を優先する
  // (明細スナップショットは商品未紐付け明細のフォールバック)
  const snp = Number(item.variation_snp ?? item.snp ?? 1);
  const quantityChanged = quantity !== new Decimal(item.quantity ?? '0').toNumber();
  // 料金を出さない発注先では単価が null になる(画面では料金を出さない)
  const baseUnitPrice = item.base_unit_price ?? item.unit_price ?? '0';
  const quantityPrices = toQuantityPriceTiers(item.quantity_prices);
  const unitPrice = quantityChanged
    ? resolveUnitPrice(baseUnitPrice, quantityPrices, quantity)
    : (item.unit_price ?? '0');
  return {
    key: item.id,
    productCode: item.product_code ?? null,
    modelNumber: item.model_number ?? null,
    name: item.name,
    manufacturer: item.manufacturer ?? null,
    spec: item.spec ?? null,
    unitPrice,
    baseUnitPrice,
    quantityPrices,
    quantity,
    snp,
    packagingTypeName: item.packaging_type_name ?? null,
    unitLabel: item.unit_name ?? null,
    amount: calculateItemAmount(unitPrice, quantity),
    taxClass: item.tax_class ?? null,
    lastPurchasedAt: item.last_purchased_at ?? null,
    desiredDeliveryDate,
    thumbnailUrl: item.thumbnail_url ?? null,
    previousUnitPrice: null,
  };
};
